/**
 * Deferred "open in a new tab" for the Edit-in-Word flow (LF-5362).
 *
 * Provisioning the Word Online session takes ~5–10s, so opening the tab in the
 * mutation's `onSuccess` runs long after the click's transient user-activation
 * has expired — Chrome (and every major browser in its default config) then
 * treats the `window.open()` as an unsolicited popup and blocks it.
 *
 * The fix is to open a placeholder tab *synchronously* inside the click handler
 * (while the user-activation is still valid), then redirect that same tab to the
 * resolved `webUrl` once provisioning finishes — or close it if provisioning
 * fails.
 */

const LOADING_DOC =
  '<!doctype html><meta charset="utf-8"><title>Opening in Word…</title>' +
  '<body style="margin:0;font-family:system-ui,-apple-system,sans-serif;' +
  'display:flex;align-items:center;justify-content:center;height:100vh;' +
  'color:#555">Opening your document in Word Online…</body>';

export interface DeferredWordTab {
  /** Redirect the placeholder tab to the resolved editing URL. Falls back to a
   *  fresh `window.open` when the placeholder was blocked or the user closed it. */
  resolve(url: string): void;
  /** Close the placeholder tab — provisioning failed, there is nothing to show. */
  cancel(): void;
}

/**
 * Open a blank tab now and hand back handles to redirect or close it later.
 *
 * `openBlankTab` is injectable purely for tests; production opens a real blank
 * `_blank` tab. Note we deliberately do **not** pass `noopener` to
 * `window.open` — that makes it return `null`, and we need the handle to
 * redirect the tab afterwards. Instead we sever the `opener` back-reference
 * ourselves (reverse-tabnabbing guard).
 */
export function openDeferredWordTab(
  openBlankTab: () => Window | null = () => window.open('', '_blank')
): DeferredWordTab {
  const tab = openBlankTab();
  if (tab) {
    tab.opener = null;
    try {
      tab.document.write(LOADING_DOC);
      tab.document.close();
    } catch {
      // about:blank is same-origin and writable, but a placeholder nicety must
      // never break the actual redirect — swallow and carry on.
    }
  }

  return {
    resolve(url: string) {
      if (tab && !tab.closed) {
        tab.location.href = url;
      } else {
        // Placeholder was blocked or closed — best-effort direct open.
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    },
    cancel() {
      tab?.close();
    }
  };
}
